import React, { useContext, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import sessionsService from '../../../services/sessionsServices';
import Toast from '../../Toast';
import { sessionsInvalidateKey } from '../../../hooks';
import { DEFAULT_PEPPER_ID } from '../../ChatSection/PolyChatHeader';
import { TemporaryChatContext } from '../../../Context/temporaryChatContext';
import { AuthContext } from '../../../Context/authContext';
import { useContextHook } from 'use-context-hook';

/**
 * Rename Chat overlay modal.
 * API unchanged: sessionsService.updateSession
 */
const RenameChatModal = ({ onClose, data }) => {
  const queryClient = useQueryClient();
  const { selectedAgent } = useContext(TemporaryChatContext);
  const { user } = useContextHook(AuthContext, v => ({ user: v.user }));

  const stripHtml = html => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.innerText.trim();
  };

  const [title, setTitle] = useState(() => stripHtml(data?.title));

  useEffect(() => {
    setTitle(stripHtml(data?.title));
  }, [data?.title]);

  const handleSubmit = useMutation({
    mutationFn: async () => {
      return await sessionsService?.updateSession({
        platform_id: 'plateslabs',
        session_id: data?.id,
        user_id: user?.data?.ID,
        agent_id: selectedAgent?.value || DEFAULT_PEPPER_ID,
        title: title.trim(),
      });
    },
    retry: 1,
    onSuccess: response => {
      Toast({
        type: 'success',
        message: response.detail,
      });
      queryClient.invalidateQueries({ queryKey: sessionsInvalidateKey });
      onClose();
    },
    onError: err => {
      Toast({
        type: 'error',
        message: err.detail,
      });
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-shell-card border border-shell-card-border rounded-xl p-6 w-full max-w-md shadow-2xl"
        onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-ink mb-1">Edit Chat Title</h3>
        <p className="text-sm text-muted mb-4">Give this chat a clearer name.</p>
        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted mb-1.5 block">Chat Title</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Enter chat title"
              className="w-full bg-white border border-shell-card-border rounded-lg px-3 py-2 text-sm text-ink placeholder:text-muted-faint outline-none focus:border-primary/40"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-muted hover:text-ink">
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleSubmit.mutate()}
              disabled={!title.trim() || handleSubmit.isPending}
              className="px-4 py-2 text-sm font-medium bg-primary hover:bg-primary/90 text-white rounded-lg disabled:opacity-50">
              {handleSubmit.isPending ? 'Updating...' : 'Update'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenameChatModal;
